<script lang="ts" setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute, useRouter} from 'vue-router';
import {logEvent} from '/src/logger';
import {setActiveLocale} from './i18n';
import {chooseLocale, languageSwitcherOptions, type LanguageSwitcherOption} from './language-switcher';
import type {Locale} from './locales';

// Compact shows each language as its flag and two-letter code (`🇩🇪 DE`), with the full name as tooltip.
const props = defineProps<{compact?: boolean}>();

const route = useRoute();
const router = useRouter();
const {t, locale} = useI18n();

const options = computed(() => languageSwitcherOptions(route.path, locale.value as Locale));

function choose(option: LanguageSwitcherOption, event: MouseEvent) {
    chooseLocale({
        from: locale.value as Locale,
        to: option.locale,
        storage: {setItem: (key, value) => localStorage.setItem(key, value)},
        log: logEvent,
    });
    // Modified clicks open the link in a new tab, which picks up the stored choice.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (option.href) {
        void router.push({path: option.href, query: route.query, hash: route.hash});
    } else {
        setActiveLocale(option.locale);
    }
}
</script>

<template>
    <nav
        data-testid="language-switcher"
        :aria-label="t('languageSwitcher.label')"
        class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <template
            v-for="option in options"
            :key="option.locale">
            <span
                v-if="option.current"
                :lang="option.locale"
                :title="props.compact ? option.label : undefined"
                :aria-label="props.compact ? option.label : undefined"
                aria-current="true"
                class="text-base-content font-semibold">
                <template v-if="props.compact">{{ option.flag }} {{ option.shortLabel }}</template>
                <template v-else>{{ option.label }}</template>
            </span>
            <a
                v-else
                :href="option.href ?? '#'"
                :hreflang="option.href ? option.locale : undefined"
                :lang="option.locale"
                :data-testid="`language-switcher-${option.locale}`"
                :title="props.compact ? option.label : undefined"
                :aria-label="props.compact ? option.label : undefined"
                class="link link-hover text-base-content/60 hover:text-base-content"
                @click="choose(option, $event)">
                <template v-if="props.compact">{{ option.flag }} {{ option.shortLabel }}</template>
                <template v-else>{{ option.label }}</template>
            </a>
        </template>
    </nav>
</template>
